import { Link, NavLink, Outlet, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { cn } from "@/lib/utils";

/**
 * Shell for all authenticated admin pages.
 * Redirects to /admin/login if no token / token expired.
 */
export function AdminLayout() {
  const { admin, isLoading, isAuthenticated, logout } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/admin/cases" className="flex items-center gap-2.5 group">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-500 flex items-center justify-center shadow-sm">
              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <span className="text-sm font-bold tracking-tight text-slate-900">
              GetCitizen <span className="text-violet-600">Admin</span>
            </span>
          </Link>

          <nav className="hidden sm:flex items-center gap-1">
            <NavItem to="/admin/cases" label="Cases" />
            <NavItem to="/admin/documents" label="Documents" />
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-slate-500">{admin?.email}</span>
            <button
              onClick={logout}
              className="text-xs font-medium text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-all"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

/** Standalone protected route — wraps pages that need admin auth. */
export function AdminProtectedRoute() {
  const { isLoading, isAuthenticated } = useAdminAuth();
  if (isLoading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-slate-200 border-t-violet-600 rounded-full animate-spin" />
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return <Outlet />;
}

/** Small reusable pill for case status */
export function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: "bg-slate-100 text-slate-500",
    in_progress: "bg-blue-50 text-blue-700",
    submitted: "bg-emerald-50 text-emerald-700",
    closed: "bg-slate-100 text-slate-400",
  };
  return (
    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full", map[status] ?? "bg-slate-100 text-slate-500")}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

/** Small reusable pill for eligibility */
export function EligibilityPill({ status }: { status?: string | null }) {
  if (!status) return null;
  const map: Record<string, string> = {
    eligible: "bg-emerald-50 text-emerald-700",
    out_of_scope: "bg-slate-100 text-slate-500",
    manual_review: "bg-amber-50 text-amber-700",
  };
  const labels: Record<string, string> = {
    eligible: "✓ Eligible",
    out_of_scope: "Out of scope",
    manual_review: "⚠ Review needed",
  };
  return (
    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full", map[status] ?? "bg-slate-100 text-slate-500")}>
      {labels[status] ?? status}
    </span>
  );
}

export function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} className={({ isActive }) =>
      cn("text-sm font-medium px-3 py-1.5 rounded-lg transition-colors",
        isActive ? "bg-violet-50 text-violet-700" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
    }>
      {label}
    </NavLink>
  );
}
